/* import __COLOCATED_TEMPLATE__ from './install-connected-app.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { type PulseAccordion } from 'glint/pulse';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type IntlService from 'embercom/services/intl';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
// @ts-ignore
import { icInterfaceIcon } from '@intercom/pulse/helpers/ic-interface-icon';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';

interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
}

interface Signature {
  Args: Args;
}

export default class InstallConnectedAppComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isDisabled() {
    return this.salesforceConfig?.isPendingSetup || false;
  }

  get isComplete() {
    return this.salesforceSetupData.hasInstalledConnectedApp;
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceConnectedAppInstallation
    );
  }

  get installationSteps() {
    return [
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.install-connected-app-section.steps.step-1',
          {
            htmlSafe: true,
            openUrl: this.connectedAppsOAuthUsageUrl,
            icon: icInterfaceIcon(['new-window'], {}),
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.install-connected-app-section.steps.step-2',
          { htmlSafe: true },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.install-connected-app-section.steps.step-3',
          { htmlSafe: true },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.install-connected-app-section.steps.step-4',
          { htmlSafe: true, userUrl: this.userUrl, icon: icInterfaceIcon(['new-window'], {}) },
        ),
      },
    ];
  }

  get connectedAppsOAuthUsageUrl() {
    return this.salesforcePlatform?.salesforceConfig?.buildlightningSetupUrl(
      'ConnectedAppsUsage/home',
    );
  }

  get userUrl() {
    return this.salesforcePlatform?.salesforceConfig?.buildlightningSetupUrl(
      `ManageUsers/page?address=/${this.salesforceConfig.userId}?noredirect=1&isUserEntityOverride=1`,
    );
  }

  @action
  async handleVisibilityChange() {
    if (document.hidden || this.salesforceSetupData.hasInstalledConnectedApp) {
      return;
    }

    await taskFor(
      this.finStandaloneService.salesforcePlatform.salesforceDataLoader
        .checkConnectedAppInstallation,
    ).perform();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Connect::InstallConnectedApp': typeof InstallConnectedAppComponent;
    'standalone/deploy/salesforce/cases/connect/install-connected-app': typeof InstallConnectedAppComponent;
  }
}
